import BaseImage from "@/app/(site)/_components/Atoms/BaseImage"
import CaptionElement from "@/app/(site)/_components/GCMolecules/CaptionElement"
import {
    getObjectFitClass,
    getObjectPositionClass
} from "@/app/(site)/_components/SanityElements/ImageElement"
import { IMAGE_DEVICE_SIZES } from "@/config/imageConfig"
import { type ImageElementType } from "@/gc-sanity-types"
import sanityLoader from "@/lib/sanityLoader"
import { getAccessibleImageAlt } from "@/src/utils/a11y/getAccessibleImageAlt"
import { clsx } from "@/src/utils/clsx"

// Art direction: a single <picture> swaps between a distinct desktop and mobile
// asset using a native <source media>, so only the matching asset is downloaded
// (no JS, SSR-safe). BaseImage renders the <img> fallback/default so existing
// next/image behaviour (optimisation, alt, blur) is reused unchanged.

// Device sizes are shared with next.config.ts (IMAGE_DEVICE_SIZES) so the
// generated <source>/<link preload> srcSets match what next/image emits for the
// <img> exactly and the browser never double-fetches. IMAGE_QUALITY is also
// passed to BaseImage below, keeping the <img> and srcSets self-consistent.
const IMAGE_QUALITY = 80

// Tailwind `md` breakpoint is 968px (see tailwind.config.ts).
const DESKTOP_MEDIA = "(min-width: 968px)"
const MOBILE_MEDIA = "(max-width: 967.98px)"

function buildSrcSet(assetRef: string): string {
    return IMAGE_DEVICE_SIZES.map(
        width =>
            `${sanityLoader({ src: assetRef, width, quality: IMAGE_QUALITY })} ${width}w`
    ).join(", ")
}

type ResponsiveImageProps = {
    desktopImage: ImageElementType
    mobileImage: ImageElementType
    isAboveTheFold?: boolean
    sizes?: string
    className?: string
}

export default function ResponsiveImage({
    desktopImage,
    mobileImage,
    isAboveTheFold,
    sizes,
    className
}: ResponsiveImageProps) {
    const desktopRef = desktopImage.image.asset?._ref || ""
    const mobileRef = mobileImage.image.asset?._ref || ""

    const desktopSrcSet = buildSrcSet(desktopRef)
    const mobileSrcSet = buildSrcSet(mobileRef)

    const caption = mobileImage.image.caption || desktopImage.image.caption
    const altText = getAccessibleImageAlt(mobileImage.image.alt, caption)

    const objectClasses = clsx(
        getObjectFitClass(mobileImage.objectSetting),
        getObjectPositionClass(mobileImage.focalPoint)
    )

    return (
        <figure className={clsx("flex h-full w-full flex-col gap-y-4", className)}>
            {isAboveTheFold && (
                <>
                    <link
                        rel="preload"
                        as="image"
                        imageSrcSet={mobileSrcSet}
                        imageSizes={sizes}
                        media={MOBILE_MEDIA}
                        fetchPriority="high"
                    />
                    <link
                        rel="preload"
                        as="image"
                        imageSrcSet={desktopSrcSet}
                        imageSizes={sizes}
                        media={DESKTOP_MEDIA}
                        fetchPriority="high"
                    />
                </>
            )}
            <picture className="relative block h-full w-full">
                <source media={DESKTOP_MEDIA} srcSet={desktopSrcSet} sizes={sizes} />
                <BaseImage
                    src={mobileRef}
                    alt={altText}
                    asset={mobileImage.image.asset}
                    fill
                    quality={IMAGE_QUALITY}
                    mimeType={mobileImage.image.asset?.mimeType || ""}
                    // Preloading for the above-the-fold case is handled by the
                    // media-scoped <link>s above so only the matching asset is
                    // fetched; next/image's own (media-less) preload would also
                    // fetch the mobile asset on desktop.
                    isAboveTheFold={false}
                    sizes={sizes}
                    className={objectClasses}
                />
            </picture>
            {caption && <CaptionElement caption={caption} />}
        </figure>
    )
}
